/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import React, { ReactNode, useCallback } from "react";

import { usePlayerController } from "../BasePlayer";

import {
  PlayerContinueWatchingProvider,
  useContinueWatchingMark,
} from "./ContinueWatching";
import { usePlayerCurrentMedia } from "./PlayerCurrentMediaContext";
import { usePlayerSource } from "./usePlayerSource";
import { useTrackAnalytics } from "./useTrackAnalytics";

interface VideoPlayerControllerProps {
  children: ReactNode;
}

export const VideoPlayerController = (props: VideoPlayerControllerProps) => {
  const { children } = props;

  const { player } = usePlayerController();
  const { playInfo, media, streamType } = usePlayerCurrentMedia();

  const source = usePlayerSource(media, playInfo, streamType);

  useContinueWatchingMark();
  useTrackAnalytics();

  const onLoadedCallback = useCallback(
    (startPosition: number, autoPlay: boolean) => {
      // Ignore start position from continue watching when loading
      // media, since we want to show popup with start over and resume
      // actions when continue watching is available, otherwise start
      // position will be 0
      source && player.loadMedia(source, autoPlay);
    },
    [player, source],
  );

  return (
    <PlayerContinueWatchingProvider onLoaded={onLoadedCallback}>
      {children}
    </PlayerContinueWatchingProvider>
  );
};
